// src/app/services/address/address.service.ts

import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { query } from '@angular/fire/firestore';

import { ApiService } from '../api/api.service';
import { AuthService } from '../auth/auth.service';
import { Address } from '../../models/address.model';

@Injectable({
  providedIn: 'root',
})
export class AddressService {
  private _addresses = new BehaviorSubject<Address[] | null>([]);
  private _addressChange = new BehaviorSubject<Address | null>(null);

  get addresses() {
    return this._addresses.asObservable();
  }

  get addressChange() {
    return this._addressChange.asObservable();
  }

  constructor(private auth: AuthService, private api: ApiService) {}

  async getUid() {
    return await this.auth.getId();
  }

  /**
   * Load addresses for current signed-in user.
   * Never queries Firestore when not signed in.
   */
  async getAddresses(limitCount?: number): Promise<Address[]> {
    try {
      const user = await this.auth.getCurrentUser();
      if (!user?.uid) {
        this._addresses.next([]);
        return [];
      }

      const uid = user.uid;

      const querySnapshot = limitCount
        ? await this.api.getDocs(`address/${uid}/all`, this.api.limitQuery(limitCount))
        : await this.api.getDocs(`address/${uid}/all`);

      const allAddress: Address[] = querySnapshot.docs.map((docSnap: any) => {
        const item: any = docSnap.data();
        item.id = docSnap.id;
        return item as Address;
      });

      this._addresses.next([...allAddress]);
      return allAddress;
    } catch (e) {
      console.log(e);
      throw e;
    }
  }

  async addAddress(param: any, no_address_change?: boolean): Promise<Address | null> {
    try {
      const uid = await this.getUid();
      if (!uid) return null;

      const data: any = new Address(
        null,
        param.title,
        param.address,
        param.landmark,
        param.house,
        param.lat,
        param.lng,
        uid
      );

      const addressData: any = { ...data };
      delete addressData.id;

      const response = await this.api.addDocument(`address/${uid}/all`, addressData);
      const id = response?.id;
      const address: Address = { ...addressData, id };

      const current = this._addresses.value ?? [];
      this._addresses.next([...(current || []), address]);

      if (!no_address_change) this._addressChange.next(address);

      return address;
    } catch (e) {
      throw e;
    }
  }

  async updateAddress(id: string, param: any, uid?: string): Promise<Address> {
    try {
      uid = uid ?? (await this.getUid());
      if (!uid) throw new Error('No auth uid');

      await this.api.updateDocument(`address/${uid}/all/${id}`, param);

      const data = new Address(
        id,
        param.title,
        param.address,
        param.landmark,
        param.house,
        param.lat,
        param.lng,
        uid
      );

      const current = this._addresses.value ?? [];
      const index = (current || []).findIndex((x: any) => x.id === id);

      if (index >= 0) {
        const next = [...(current || [])];
        next[index] = data as any;
        this._addresses.next(next);
      } else {
        this._addresses.next([...(current || []), data as any]);
      }

      return data as any;
    } catch (e) {
      throw e;
    }
  }

  async deleteAddress(param: any): Promise<Address[]> {
    try {
      const uid = await this.getUid();
      if (!uid) throw new Error('No auth uid');

      await this.api.deleteDocument(`address/${uid}/all/${param.id}`);

      const current = this._addresses.value ?? [];
      const next = (current || []).filter((x: any) => x.id !== param.id);

      this._addresses.next(next);
      return next as any;
    } catch (e) {
      throw e;
    }
  }

  changeAddress(address: Address | null) {
    this._addressChange.next(address);
  }

  getSelectedAddress() {
    return this._addressChange.value;
  }

  /**
   * Checks if given lat/lng already exists in user's address list.
   * If exists, selects the existing address; otherwise selects the incoming location object.
   */
  async checkExistAddress(location: any): Promise<Address | null> {
    try {
      if (!location || typeof location.lat !== 'number' || typeof location.lng !== 'number') {
        this.changeAddress(null);
        return null;
      }

      const uid = await this.getUid();
      if (!uid) {
        // not signed in → do not query
        const loc = location as Address;
        this.changeAddress(loc);
        return loc;
      }

      const dataRef = this.api.collectionFirestoreRef(`address/${uid}/all`);
      const q = query(
        dataRef,
        this.api.whereQuery('lat', '==', location.lat),
        this.api.whereQuery('lng', '==', location.lng)
      );

      const querySnapshot = await this.api.getDocsByQuery(q);

      const matches: Address[] = querySnapshot.docs.map((docSnap: any) => {
        const item: any = docSnap.data();
        item.id = docSnap.id;
        return item as Address;
      });

      const selected = matches.length > 0 ? matches[0] : (location as Address);
      this.changeAddress(selected);
      return selected;
    } catch (e) {
      throw e;
    }
  }

  reset() {
    this._addresses.next([]);
    this._addressChange.next(null);
  }
}
